import { type Session } from '@supabase/supabase-js'
import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { initOneSignal, logoutOneSignal } from '@/lib/native/push/onesignal'

import { supabase } from './client'

type AuthCtx = {
  session: Session | null
  loading: boolean
  signOut: () => Promise<void>
  getAccessToken: () => string | null
}

const Ctx = createContext<AuthCtx | null>(null)

export function AuthProvider({ children }: PropsWithChildren) {
  const [session, setSession] = useState<Session | null>(null)
  const [loading, setLoading] = useState(true)
  const tokenRef = useRef<string | null>(null)

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session)
      tokenRef.current = data.session?.access_token ?? null
      if (data.session) {
        initOneSignal(data.session.user.id).catch(() => undefined)
      }
      setLoading(false)
    })
    const { data: sub } = supabase.auth.onAuthStateChange((_event, s) => {
      setSession(s)
      tokenRef.current = s?.access_token ?? null
      if (s) {
        initOneSignal(s.user.id).catch(() => undefined)
      } else {
        logoutOneSignal()
      }
    })
    return () => {
      sub.subscription.unsubscribe()
    }
  }, [])

  const signOut = useCallback(async () => {
    await supabase.auth.signOut()
  }, [])

  const getAccessToken = useCallback(() => tokenRef.current, [])

  const value = useMemo(
    () => ({ session, loading, signOut, getAccessToken }),
    [session, loading, signOut, getAccessToken],
  )

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useAuth() {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
